// Sugalarm — Cycle 74b.1: inline SVG food glyphs for the Now-screen
// IOB meal lines. Replaces the cycle-74b emoji, which rendered as
// blank tofu rectangles on the kiosk (Chromium under `cage` has no
// color-emoji font). These are simple monochrome line glyphs drawn
// with `currentColor`, so they inherit the line's ink and render
// identically on kiosk / web / mobile / iOS regardless of fonts.

import { foodIconKey, type FoodIconKey } from "../../lib/foodEmoji";

interface Props {
  /** Resolve the glyph from a free-text note (keyword match). */
  notes?: string | null | undefined;
  /** Cycle 75: a pre-resolved icon key (from a MealContextBlock).
   *  Takes precedence over `notes` so the caller can reuse the key it
   *  already computed instead of re-running the keyword match. */
  iconKey?: FoodIconKey;
  size?: number;
}

// Each glyph is authored against an 18×18 viewBox, stroke = currentColor.
function paths(key: FoodIconKey): JSX.Element {
  const s = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.4,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (key) {
    case "pizza":
      return (
        <>
          <path d="M9 2.5 L15.5 13 Q9 16 2.5 13 Z" {...s} />
          <circle cx="7" cy="9" r="0.9" fill="currentColor" stroke="none" />
          <circle cx="10.5" cy="10.5" r="0.9" fill="currentColor" stroke="none" />
        </>
      );
    case "pancake":
      return (
        <>
          <ellipse cx="9" cy="6.5" rx="6" ry="2" {...s} />
          <path d="M3 6.5 v2.5 Q3 11 9 11 Q15 11 15 9 v-2.5" {...s} />
          <path d="M3 9.5 v2.5 Q3 14 9 14 Q15 14 15 12 v-2.5" {...s} />
          <path d="M9 4.5 v-2 M9 2.5 q2 0 2 1.5" {...s} />
        </>
      );
    case "noodle":
      return (
        <>
          <path d="M3 8 Q9 15 15 8" {...s} />
          <path d="M3 8 H15" {...s} />
          <path d="M6 8 q0 -4 3 -4 q3 0 3 4" {...s} />
          <path d="M9 4 v-1.5" {...s} />
        </>
      );
    case "soup":
      return (
        <>
          <path d="M2.5 8 Q9 15 15.5 8 Z" {...s} />
          <path d="M2.5 8 H15.5" {...s} />
          <path d="M7 5 q1 -1.5 0 -3 M11 5 q1 -1.5 0 -3" {...s} />
        </>
      );
    case "rice":
      return (
        <>
          <path d="M3 8 Q9 14.5 15 8 Z" {...s} />
          <path d="M3 8 H15" {...s} />
          <path d="M5 6 q4 -3 8 0" {...s} />
          <circle cx="7" cy="5.5" r="0.7" fill="currentColor" stroke="none" />
          <circle cx="9" cy="5" r="0.7" fill="currentColor" stroke="none" />
          <circle cx="11" cy="5.5" r="0.7" fill="currentColor" stroke="none" />
        </>
      );
    case "cereal":
      return (
        <>
          <path d="M2.5 8 Q9 14.5 15.5 8 Z" {...s} />
          <path d="M2.5 8 H15.5" {...s} />
          <path d="M12 7 L16 4" {...s} />
          <circle cx="16" cy="3.5" r="1.4" {...s} />
        </>
      );
    case "salad":
      return (
        <>
          <path d="M2.5 8 Q9 14.5 15.5 8 Z" {...s} />
          <path d="M2.5 8 H15.5" {...s} />
          <path d="M6 7 q-1 -3 2 -4 q1 2 -1 4" {...s} />
          <path d="M11 7 q2 -2.5 1 -4.5" {...s} />
        </>
      );
    case "taco":
      return (
        <>
          <path d="M2.5 13 Q2.5 5.5 9 5.5 Q15.5 5.5 15.5 13 Z" {...s} />
          <path d="M2.5 9 H15.5" {...s} />
          <circle cx="6.5" cy="11" r="0.8" fill="currentColor" stroke="none" />
          <circle cx="9.5" cy="11.5" r="0.8" fill="currentColor" stroke="none" />
        </>
      );
    case "burger":
      return (
        <>
          <path d="M3 6 Q3 3 9 3 Q15 3 15 6 Z" {...s} />
          <path d="M3.5 8.5 H14.5" {...s} />
          <path d="M3 11 Q3 14 9 14 Q15 14 15 11 H3 Z" {...s} />
        </>
      );
    case "sandwich":
      return (
        <>
          <path d="M2.5 13.5 L9 3.5 L15.5 13.5 Z" {...s} />
          <path d="M6 9 H12" {...s} />
        </>
      );
    case "fries":
      return (
        <>
          <path d="M5 8 H13 L12 15.5 H6 Z" {...s} />
          <path d="M5 8 H13" {...s} />
          <path d="M7 8 V4 M9 8 V2.5 M11 8 V4.5" {...s} />
        </>
      );
    case "dessert":
      return (
        <>
          <path d="M6 8 L9 16 L12 8" {...s} />
          <path d="M5.5 8 H12.5" {...s} />
          <circle cx="7.5" cy="6" r="2.3" {...s} />
          <circle cx="10.5" cy="6" r="2.3" {...s} />
        </>
      );
    case "egg":
      return (
        <>
          <path d="M6 13 Q2.5 13 3 9.5 Q3.3 6 6.5 5.5 Q7.5 3 10 3.5 Q13 4 13 7.5 Q15 9 14 11.5 Q13 13.5 10 13 Z" {...s} />
          <circle cx="8.5" cy="8.5" r="2.2" fill="currentColor" stroke="none" />
        </>
      );
    case "chicken":
      return (
        <>
          <path d="M11.5 4.5 Q15 5 14.5 8.5 Q14 11 11 11 Q9 13 6.5 12.5 Q4.5 12 5 9.5 Q5.5 6 8.5 5.5 Q9.5 4 11.5 4.5 Z" {...s} />
          <path d="M6 11.5 L3 14.5 M5 12.5 L3.5 11" {...s} />
        </>
      );
    case "coffee":
      return (
        <>
          <path d="M4 7 H12 V11 Q12 14 8 14 Q4 14 4 11 Z" {...s} />
          <path d="M12 8 Q15 8 15 10 Q15 12 12 12" {...s} />
          <path d="M7 4.5 q1 -1.5 0 -2.5 M10 4.5 q1 -1.5 0 -2.5" {...s} />
        </>
      );
    case "fruit":
      return (
        <>
          <path d="M9 5.5 Q6 4.5 4.5 7 Q3 9.5 4.5 12.5 Q6 15.5 9 14.5 Q12 15.5 13.5 12.5 Q15 9.5 13.5 7 Q12 4.5 9 5.5 Z" {...s} />
          <path d="M9 5.5 V3 Q9 2 11 2.5" {...s} />
        </>
      );
    case "meal":
    default:
      return (
        <>
          {/* fork */}
          <path d="M5 3 V6.5 Q5 7.5 6.5 7.5 Q8 7.5 8 6.5 V3" {...s} />
          <path d="M6.5 7.5 V15" {...s} />
          {/* knife */}
          <path d="M12 3 Q13.6 3 13.6 6 Q13.6 8.5 12 8.5 V15" {...s} />
        </>
      );
  }
}

export function FoodGlyph({ notes, iconKey, size = 14 }: Props) {
  const key = iconKey ?? foodIconKey(notes);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      aria-hidden="true"
      style={{ flexShrink: 0, display: "block" }}
    >
      {paths(key)}
    </svg>
  );
}
